import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ConsentStatus = 'unset' | 'accepted' | 'declined';

/**
 * Route prefixes that make up the authenticated app (all AuthGuard-protected).
 * Reaching any of these implies a signed-in subscriber, so GA is enabled there
 * (their consent basis is the checkout agreement) and the public cookie banner
 * is suppressed. Route-based only — this makes no auth/Cognito calls.
 */
export const AUTHENTICATED_ROUTE_PREFIXES = [
  '/dashboard',
  '/profile',
  '/onboarding',
  '/app',
];

export function isAuthenticatedPath(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  return AUTHENTICATED_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Tracks the visitor's cookie-consent choice and persists it in localStorage.
 * Nothing here loads a script or sets a tracking cookie — it only records the
 * decision. Default is 'unset': no consent, no tracking.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly storageKey = 'ra_cookie_consent';
  private readonly subject = new BehaviorSubject<ConsentStatus>(this.readStored());

  /** Current status, emitted now and on every change. */
  readonly status$: Observable<ConsentStatus> = this.subject.asObservable();

  get status(): ConsentStatus {
    return this.subject.value;
  }

  /** True once the visitor has chosen (Accept or Decline). */
  get hasChosen(): boolean {
    return this.status !== 'unset';
  }

  accept(): void {
    this.persist('accepted');
  }

  decline(): void {
    this.persist('declined');
  }

  private persist(value: ConsentStatus): void {
    try {
      localStorage.setItem(this.storageKey, value);
    } catch {
      // Private mode / storage blocked — the choice holds for this page view.
    }
    this.subject.next(value);
  }

  private readStored(): ConsentStatus {
    try {
      const v = localStorage.getItem(this.storageKey);
      return v === 'accepted' || v === 'declined' ? v : 'unset';
    } catch {
      return 'unset';
    }
  }
}
